import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import Lenis from 'lenis'

const LenisContext = createContext<Lenis | null>(null)

export function useLenis() {
  return useContext(LenisContext)
}

export function LenisProvider({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    // Skip Lenis on mobile — native momentum scrolling is better and
    // Lenis's scroll virtualisation breaks CSS scroll-snap on iOS Safari.
    if (window.innerWidth < 768) return

    const instance = new Lenis({
      lerp: 0.075,
      smoothWheel: true,
    })
    // eslint-disable-next-line react-hooks/set-state-in-effect -- canonical external-system case: Lenis owns the scroll and must be constructed against a live DOM, then published to consumers via context. There is no render-time equivalent.
    setLenis(instance)

    const raf = (time: number) => {
      instance.raf(time)
      rafRef.current = requestAnimationFrame(raf)
    }
    rafRef.current = requestAnimationFrame(raf)

    return () => {
      cancelAnimationFrame(rafRef.current)
      instance.destroy()
      setLenis(null)
    }
  }, [])

  return (
    <LenisContext.Provider value={lenis}>
      {children}
    </LenisContext.Provider>
  )
}
